import React, { useState, useEffect, useRef } from "react";
import Phaser from "phaser";
import playerImage from "./images/tiny_ship11.png"; // Ensure this path is correct
import {
  db,
  collection,
  getDocs,
  addDoc,
  query,
  orderBy,
  limit,
} from "./firebase.js";
import { useNavigate } from "react-router-dom";

const Game = () => {
  const gameRef = useRef(null);

  const [playerName, setPlayerName] = useState("");
  const [scoreFinal, setScore] = useState("");
  const [gameStarted, setGameStarted] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false); // State to toggle leaderboard visibility
  let score = 0;
  const navigate = useNavigate();

  const handleStartGame = () => {
    if (playerName.trim()) {
      setGameStarted(true);
    } else {
      alert("Please enter a player name.");
    }
  };

  const showLeaderboard = () => {
    setShowLeaderboardModal(true); // Show leaderboard modal
  };

  const hideLeaderboard = () => {
    setShowLeaderboardModal(false); // Hide leaderboard modal
  };

  useEffect(() => {
    const fetchLeaderboard = async () => {
      const scoresCollection = collection(db, "highScores");
      const q = query(scoresCollection, orderBy("score", "desc"), limit(10)); // Get top 10 scores
      const querySnapshot = await getDocs(q);
      const scores = querySnapshot.docs.map((doc) => doc.data());
      setLeaderboard(scores);
    };

    fetchLeaderboard();
  }, []);

  const submitScore = async () => {
    if (score > 0 && playerName) {
      // Add the new score to Firestore
      await addDoc(collection(db, "highScores"), {
        player: playerName,
        score: score,
      });

      // Fetch updated leaderboard
      const scoresCollection = collection(db, "highScores");
      const q = query(scoresCollection, orderBy("score", "desc"), limit(10)); // Get top 10 scores
      const querySnapshot = await getDocs(q);
      const scores = querySnapshot.docs.map((doc) => doc.data());

      // Update the leaderboard state
      setLeaderboard(scores);
    }
  };

  useEffect(() => {
    let player;
    let bullets;
    let enemies;
    let cursors;
    let scoreText;
    let touchMove = { x: 0 }; // For mobile movement
    let touchMoveEnabled = false;

    if (!gameStarted) return; // Ensure game is not created until gameStarted is true

    // Game config
    const config = {
      type: Phaser.AUTO,
      width: window.innerWidth,
      height: window.innerHeight,
      scene: {
        preload,
        create,
        update,
      },
      physics: {
        default: "arcade",
        arcade: {
          gravity: { y: 0 },
          debug: false,
        },
      },
      scale: {
        mode: Phaser.Scale.RESIZE, // Ensure scaling for mobile
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
    };

    // Preload assets
    function preload() {
      this.load.image("player", playerImage);
      this.load.image("bullet", "path/to/bullet/image"); // Ensure you have a bullet image
    }

    // Create game objects
    function create() {
      // Player setup
      player = this.physics.add
        .image(window.innerWidth / 2, window.innerHeight - 100, "player")
        .setCollideWorldBounds(true);

      // Bullet setup
      bullets = this.physics.add.group({
        defaultKey: "bullet",
        maxSize: 10,
      });

      // Enemy setup
      enemies = this.physics.add.group();

      // Spawn enemies every 2 seconds
      this.time.addEvent({
        delay: 2000,
        callback: spawnEnemy,
        callbackScope: this,
        loop: true,
      });

      // Score text setup
      scoreText = this.add.text(20, 20, `Score: ${score}`, {
        fontSize: "20px",
        fill: "#fff",
      });

      // Controls setup for desktop
      cursors = this.input.keyboard.createCursorKeys();
      this.input.keyboard.on("keydown-R", shootBullet, this); // R key to shoot

      // Mobile movement setup
      this.input.on("pointermove", (pointer) => {
        if (touchMoveEnabled) {
          touchMove.x = pointer.x;
        }
      });

      // Mobile shooting setup
      this.input.on("pointerdown", (pointer) => {
        if (pointer.y > window.innerHeight - 100) {
          // Check if it's in the shoot area
          shootBullet();
        }
      });

      // Collision handling
      this.physics.add.collider(bullets, enemies, hitEnemy, null, this);
      this.physics.add.collider(enemies, player, gameOver, null, this);

      // Setup touch buttons for mobile
      createMobileControls(this);
    }

    // Update game loop
    function update() {
      // Player movement for desktop
      if (cursors.left.isDown) {
        player.setVelocityX(-200);
      } else if (cursors.right.isDown) {
        player.setVelocityX(200);
      } else {
        player.setVelocityX(0);
      }

      // Player movement for mobile
      if (touchMoveEnabled) {
        const moveSpeed = 200;
        player.setVelocityX(0); // Reset player velocity in X direction first
        if (touchMove.x < player.x - 10) {
          player.setVelocityX(-moveSpeed); // Move left
        } else if (touchMove.x > player.x + 10) {
          player.setVelocityX(moveSpeed); // Move right
        }
      }
    }

    // Bullet shooting function
    function shootBullet() {
      if (bullets.countActive(true) < 10) {
        const bullet = bullets.get(player.x, player.y - 30);
        if (bullet) {
          bullet.setActive(true).setVisible(true);
          bullet.setTexture("bullet"); // Bullet texture
          bullet.setSize(5, 20); // Bullet size
          bullet.setVelocityY(-300); // Bullet speed
        }
      }
    }

    // Spawn enemy function
    function spawnEnemy() {
      const enemyGraphics = this.add.graphics();
      enemyGraphics.fillStyle(Phaser.Math.Between(0x0000ff, 0x00ff00), 1); // Random color
      enemyGraphics.fillRect(0, 0, 50, 50); // Size of the enemy

      const enemy = this.physics.add.existing(enemyGraphics);
      enemy.body.setVelocityY(Phaser.Math.Between(100, 200)); // Set enemy velocity
      enemy.setPosition(Phaser.Math.Between(50, window.innerWidth - 50), 0); // Position

      this.physics.add.collider(bullets, enemy, hitEnemy, null, this);
      this.physics.add.collider(player, enemy, gameOver, null, this);
    }

    // Bullet hits enemy
    function hitEnemy(bullet, enemy) {
      bullet.setActive(false).setVisible(false);
      enemy.setActive(false).setVisible(false);
      score += 10;
      scoreText.setText(`Score: ${score}`);
    }

    // Game Over function
    function gameOver(player, enemy) {
      player.setTint(0xff0000);
      this.physics.pause();
      scoreText.setText(`Game Over! Final Score: ${score}`);
      setScore(score);
      setIsGameOver(true); // Set game over state to true

      // Automatically submit the score after game over
      submitScore();
    }

    // Function to create touch controls for mobile
    function createMobileControls(scene) {
      // Movement buttons
      const leftButton = scene.add
        .text(50, window.innerHeight - 150, "←", {
          fontSize: "32px",
          fill: "#fff",
          backgroundColor: "#000",
        })
        .setInteractive()
        .on("pointerdown", () => {
          touchMoveEnabled = true;
          touchMove.x -= 100;
        });

      const rightButton = scene.add
        .text(window.innerWidth - 100, window.innerHeight - 150, "→", {
          fontSize: "32px",
          fill: "#fff",
          backgroundColor: "#000",
        })
        .setInteractive()
        .on("pointerdown", () => {
          touchMoveEnabled = true;
          touchMove.x += 100;
        });

      // Shooting button
      const shootButton = scene.add
        .text(window.innerWidth - 100, window.innerHeight - 50, "Shoot", {
          fontSize: "32px",
          fill: "#fff",
          backgroundColor: "#f00",
        })
        .setInteractive()
        .on("pointerdown", shootBullet);
    }

    // Create the Phaser game instance
    const game = new Phaser.Game(config);
    gameRef.current = game;

    return () => {
      // Cleanup the game instance when component is unmounted
      if (gameRef.current) {
        gameRef.current.destroy(true);
      }
    };
  }, [gameStarted]); // Dependency on gameStarted

  return (
    <div>
      {isGameOver && (
        <div className="game-over-modal">
          <h2>Game Over</h2>
          <p>Final Score: {scoreFinal}</p>
          <button onClick={() => navigate("/")}>Home</button>
          <button onClick={() => window.location.reload()}>Retry</button>
          <button onClick={showLeaderboard}>Leaderboard</button>
        </div>
      )}

      {showLeaderboardModal && (
        <div className="leaderboard">
          <h3>Leaderboard</h3>
          <ul>
            {leaderboard.map((entry, index) => (
              <li key={index}>
                {entry.player}: {entry.score}
              </li>
            ))}
          </ul>
          <button onClick={hideLeaderboard}>Close</button>
        </div>
      )}

      {!gameStarted ? (
        <div className="flex flex-col justify-center items-center h-screen text-center">
          <h1 className="text-2xl mb-4">Enter Player Name</h1>
          <input
            type="text"
            placeholder="Player Name"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            className="text-green p-2 mt-2 text-lg w-64 text-center bg-black border border-green-300 rounded"
          />
          <button
            onClick={handleStartGame}
            className="p-2 mt-4 text-lg bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Start Game
          </button>
        </div>
      ) : (
        <div id="game-container" />
      )}
    </div>
  );
};

export default Game;
